// 本人確認の枠の一覧を扱う共通の器。日本在住フォームと海外在住フォームの両方が使い、
// 枠ごとの表示値・撮影の開始と終了・失敗モーダル・「次へ」の可否を返す
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { RetakeFailureDisplay, retakeDisplayForReason } from '@/components/molecules/checkIn/identity-verify-error-dialog'
import { IdentityVerifyCameraGuide, IdentityVerifyCameraShutter } from '@/components/organisms/checkIn/identity-verify-camera'
import {
  IdentificationSlot,
  finishIdentityVerify,
  dismissIdentificationFailure,
  removeIdentificationImage,
  resetIdentification,
  pickUploadReservationUuid,
  submitIdentificationImage,
} from '@/features/checkIn/identification-slice'
import { useIdentityVerifyNextPath } from '@/hooks/use-identity-verify-navigation'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

export type DocumentSlotDefinition = {
  slot: IdentificationSlot
  label?: string
  sampleImageUrl?: string
  boxHeightPx?: number
  cameraInstruction: string
}

export type DocumentCameraPreset = {
  guide: IdentityVerifyCameraGuide
  guideImageUrl?: string
  shutter: IdentityVerifyCameraShutter
  autoDetect?: (canvas: HTMLCanvasElement) => Promise<boolean>
}

export type DocumentSlotProps = {
  image?: { previewUrl: string }
  loading: boolean
  label?: string
  sampleImageUrl?: string
  boxHeightPx?: number
  onSelectFile: (file: File) => void
  onRequestCamera: () => void
}

export type DocumentSlotView = DocumentSlotProps & { slotId: string }

export const useDocumentSlots = (params: {
  hotelId: string
  requiredIdentifyVerification: 0 | 1
  visibleSlotDefinitions: DocumentSlotDefinition[]
  everyPossibleSlotId: string[]
  cameraPreset: DocumentCameraPreset
  retakeDisplay: RetakeFailureDisplay
}) => {
  const dispatch = useAppDispatch()
  const { goNext } = useIdentityVerifyNextPath()
  const images = useAppSelector(state => state.checkIn.identification.images)
  const submitting = useAppSelector(state => state.checkIn.identification.submitting)
  const failure = useAppSelector(state => state.checkIn.identification.failure)
  const selectedReservations = useAppSelector(state => state.checkIn.checkInData?.fields?.selectedReservations)

  const [activeSlotId, setActiveSlotId] = useState<string>()

  const definedSlotIds = params.visibleSlotDefinitions.map(definition => definition.slot.slotId)

  useEffect(() => {
    const keep = new Set(definedSlotIds)
    params.everyPossibleSlotId.filter(id => !keep.has(id)).forEach(id => dispatch(removeIdentificationImage({ slotId: id })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definedSlotIds.join(',')])

  // 失敗の通知は画面に 1 つしか持たないため、離れるときに消す。残すと次に来たときに脈絡なく開く
  useEffect(() => () => void dispatch(dismissIdentificationFailure()), [dispatch])

  const submit = (slot: IdentificationSlot, file: Blob, fileName: string, isManual: boolean) =>
    dispatch(
      submitIdentificationImage({
        hotelId: params.hotelId,
        reservationUuid: pickUploadReservationUuid(selectedReservations, dayjs().format('YYYY-MM-DD')),
        slot,
        file,
        fileName,
        isManual,
        requiredIdentifyVerification: params.requiredIdentifyVerification,
      }),
    ).then(result => (submitIdentificationImage.fulfilled.match(result) ? undefined : result.payload?.reason))

  const buildSlotProps = (definition: DocumentSlotDefinition): DocumentSlotProps => {
    const slotId = definition.slot.slotId
    const image = images[slotId]
    return {
      image: image && { previewUrl: image.previewUrl },
      loading: !!submitting[slotId],
      label: definition.label,
      sampleImageUrl: definition.sampleImageUrl,
      boxHeightPx: definition.boxHeightPx,
      onSelectFile: file => void submit(definition.slot, file, file.name, true),
      onRequestCamera: () => setActiveSlotId(slotId),
    }
  }

  const propsBySlotId = new Map(params.visibleSlotDefinitions.map(definition => [definition.slot.slotId, buildSlotProps(definition)]))

  const slotProps = (slotId: string): DocumentSlotProps => {
    const props = propsBySlotId.get(slotId)
    if (!props) {
      throw new Error(`useDocumentSlots: slot "${slotId}" is not in visibleSlotDefinitions`)
    }
    return props
  }

  const slotViews: DocumentSlotView[] = params.visibleSlotDefinitions.map(definition => ({
    slotId: definition.slot.slotId,
    ...slotProps(definition.slot.slotId),
  }))

  const activeDefinition = params.visibleSlotDefinitions.find(definition => definition.slot.slotId === activeSlotId)

  // この画面の枠でない失敗（別の書類種別へ切り替えた後に届いた等）は表示しない
  const displayedFailure =
    failure && definedSlotIds.includes(failure.slotId)
      ? {
          slotId: failure.slotId,
          retake: retakeDisplayForReason(failure.reason, params.retakeDisplay),
          onDismiss: () => dispatch(dismissIdentificationFailure()),
        }
      : undefined

  // 送信中と失敗モーダル表示中は自動判定を止める。動かすと送信が二重になり、枠の状態が上書きされる
  const isAutoDetectSuspended = !!displayedFailure || (!!activeSlotId && !!submitting[activeSlotId])

  return {
    slotProps,
    slotViews,
    captureLayer: {
      cameraPreset: isAutoDetectSuspended ? { ...params.cameraPreset, autoDetect: undefined } : params.cameraPreset,
      activeCapture: activeDefinition && {
        instruction: activeDefinition.cameraInstruction,
        onCapture: async (blob: Blob, isManual: boolean) => {
          const reason = await submit(activeDefinition.slot, blob, `${activeDefinition.slot.slotId}.jpeg`, isManual)
          if (reason === undefined) {
            setActiveSlotId(undefined)
          }
        },
        onCancel: () => setActiveSlotId(undefined),
      },
      failure: displayedFailure,
    },
    isReadyToProceed: params.visibleSlotDefinitions.every(
      definition => !!images[definition.slot.slotId] && !submitting[definition.slot.slotId],
    ),
    // 在住地はここでは確定させない。確定させると、直前にアップロードした自分の画像まで
    // 「切り替わった」ものとして捨てられてしまう
    next: () => {
      dispatch(finishIdentityVerify())
      goNext()
    },
    skip: () => {
      // 済んだ印は reset の後に立てる（先に立てると reset で一緒に消える）
      dispatch(resetIdentification())
      dispatch(finishIdentityVerify())
      goNext()
    },
  }
}
